import React from "react";

import useRemoveMessagesAfterStream from "services/dbApi/hooks/useRemoveMessagesAfterStream";
import { PRODUCT_NAME } from "utilities/productName";
import { RemoveFileMessageContext, UploadContext } from "./UploadContext";
import type { UploadProgressMap } from "./UploadContext";

interface UploadProviderProps {
  children?: React.ReactNode;
}

/**
 * Allows the app to track upload progress of files across multiple chat threads.
 * Components should use the `useUploadProgress` hook to interact with this context.
 */
export function UploadProvider({ children }: UploadProviderProps) {
  const context = React.useState<UploadProgressMap>({});

  return (
    <UploadContext.Provider value={context}>
      <RemoveFileMessageProvider>{children}</RemoveFileMessageProvider>
    </UploadContext.Provider>
  );
}

/**
 * Owns `handleDelete`'s messages PUT. The delete button unmounts with its
 * message before the PUT settles, and only a mounted component can throw a
 * MessagesWriteError (ADR 065). Separate from `UploadProvider`, so the PUT's
 * state changes don't re-render every `useUploadProgress` caller.
 */
function RemoveFileMessageProvider({ children }: UploadProviderProps) {
  const removeMessagesAfterStream = useRemoveMessagesAfterStream({
    errorTitle: "Couldn't delete the file",
    errorDetail: `${PRODUCT_NAME} couldn't save your change after several attempts. Nothing already saved is affected.`,
  });

  return (
    <RemoveFileMessageContext.Provider value={removeMessagesAfterStream}>
      {children}
    </RemoveFileMessageContext.Provider>
  );
}
